import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  CashbackGamesItem,
  handlePaginateGames,
} from "store/liveOps/cashbackGames/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import Button from "components/buttons/Button";
import SearchInput from "components/inputs/SearchInput/SearchInput";

import { Modal } from "components/Modal/Modal";
import styles from "./GameActionModal.module.scss";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

const AddToCashback: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isFetched, setFetched] = useState<boolean>(false);
  const [cashbacksList, setCashbacksList] = useState<CashbackGamesItem[]>([]);
  const [cashbacksListFull, setCashbacksListFull] = useState<
    CashbackGamesItem[]
  >([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedCashback, setSelectedCashback] = useState<number>();
  const selectedItems = useAppSelector(
    (store) => store["cashback-games"].selectedItemsGames,
  );

  const handleLoadCashbacks = async () => {
    setFetched(false);
    try {
      const result = await instance.get(
        `${apiRoutes.cashbacksGamesList}?limit=1000&offset=0`,
      );
      setCashbacksListFull(result.data.items);
    } catch {
      console.log("handleLoadCashbacks error");
    }
    setFetched(true);
  };

  useEffect(() => {
    handleLoadCashbacks();
  }, []);

  useEffect(() => {
    setCashbacksList(
      cashbacksListFull.filter((el) =>
        el.title?.toLowerCase().includes(searchValue?.toLowerCase()),
      ),
    );
  }, [cashbacksListFull, searchValue]);

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
  };

  const handleApply = async () => {
    try {
      await instance.put(`game/cashback/${selectedCashback ?? 0}/add-games`, {
        gameIds: selectedItems,
      });
      dispatch(
        showNotification({
          title: t("success"),
          message: t("addGamesSuccess"),
          type: NotificationTypes.success,
        }),
      );
      handleCloseClick();
      dispatch(handlePaginateGames(1));
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("addGamesError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  const handleItemClick = (id: number) => {
    if (selectedCashback === id) {
      setSelectedCashback(undefined);
    } else {
      setSelectedCashback(id);
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header className={styles.addToCashbackTitle}>
        {t("gamesAddToCashback")}
      </Modal.Header>
      <Modal.Content
        className={`${styles.addToCashbackContentWrapper} ${styles.addToLiveOpsEntityContentWrapper}`}
      >
        {isFetched ? (
          <div>
            <div className="games-order-search">
              <SearchInput
                placeholder={t("searchCashbackList")}
                value={searchValue}
                onChange={onSearch}
                isSmall
              />
            </div>
            <div className="games-order-list segments-order-list wagers-order-list">
              {isFetched ? (
                <>
                  <div className="item">
                    <div className="id">ID</div>
                    <div className="name">{t("name").toUpperCase()}</div>
                    <div className="type">{t("type").toUpperCase()}</div>
                    <div className="description">
                      {t("description").toUpperCase()}
                    </div>
                  </div>
                  {cashbacksList.map((el) => (
                    <div
                      className={`item ${selectedCashback === el.id ? "active" : ""}`}
                      key={el.id}
                      onClick={() => {
                        handleItemClick(el.id ?? 0);
                      }}
                    >
                      <div className="id">{el.id}</div>
                      <div className="name">{el.title}</div>
                      <div className="type">{el.cashBackType}</div>
                      <div className="description">{el.description}</div>
                    </div>
                  ))}
                </>
              ) : (
                <ModalTabSkeleton />
              )}
            </div>
          </div>
        ) : (
          <ModalTabSkeleton />
        )}
      </Modal.Content>
      <Modal.Footer>
        <Button isSecondary txt={t("cancel")} handleClick={handleCloseClick} />
        <Button isPrimary handleClick={handleApply} txt={t("save")} />
      </Modal.Footer>
    </Modal>
  );
};

export default AddToCashback;
